import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import { 
  Store, DollarSign, TrendingUp, Users, Copy, Check, 
  ChevronRight, Calendar, Clock, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function SalesDashboard() {
  const { api, user } = useAuth();
  const [stats, setStats] = useState({
    totalRestaurants: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    monthlyEarnings: 0
  });
  const [restaurants, setRestaurants] = useState([]);
  const [recentCommissions, setRecentCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      // Get salesperson's restaurants
      const restResponse = await api.get('/restaurants');
      const myRestaurants = restResponse.data.filter(r => r.sales_person_id === user?.id);
      setRestaurants(myRestaurants);

      // Get earnings data
      try {
        const earningsResponse = await api.get('/salesperson/earnings');
        setStats({
          totalRestaurants: myRestaurants.length,
          totalEarnings: earningsResponse.data.total_earnings || 0,
          pendingEarnings: earningsResponse.data.pending_earnings || 0,
          monthlyEarnings: earningsResponse.data.monthly_earnings || 0
        });
        setRecentCommissions(earningsResponse.data.recent_commissions || []);
      } catch (e) {
        // Fallback if earnings endpoint doesn't exist
        setStats(prev => ({
          ...prev,
          totalRestaurants: myRestaurants.length
        }));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyReferralCode = () => {
    if (user?.referral_code) {
      navigator.clipboard.writeText(user.referral_code);
      setCopied(true);
      toast.success('Referral code copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-slate-200 rounded w-48 animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Welcome back, {user?.name?.split(' ')[0]}!</h1>
          <p className="text-slate-500 mt-1">Here's your sales performance overview</p>
        </div>
        
        {/* Referral Code Card */}
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
          <CardContent className="py-4">
            <p className="text-purple-100 text-sm mb-1">Your Referral Code</p>
            <div className="flex items-center gap-3">
              <code className="text-xl font-mono font-bold">{user?.referral_code || 'N/A'}</code>
              <Button 
                variant="secondary" 
                size="sm" 
                onClick={copyReferralCode}
                className="bg-white/20 hover:bg-white/30 text-white border-0"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">My Restaurants</p>
                <p className="text-3xl font-bold text-blue-700">{stats.totalRestaurants}</p>
                <p className="text-xs text-blue-500">Active partners</p>
              </div>
              <Store className="w-10 h-10 text-blue-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Earnings</p>
                <p className="text-3xl font-bold text-green-700">{formatCurrency(stats.totalEarnings)}</p>
                <p className="text-xs text-green-500">Lifetime commission</p>
              </div>
              <DollarSign className="w-10 h-10 text-green-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-600">Pending</p>
                <p className="text-3xl font-bold text-amber-700">{formatCurrency(stats.pendingEarnings)}</p>
                <p className="text-xs text-amber-500">Awaiting payout</p>
              </div>
              <Clock className="w-10 h-10 text-amber-300" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">This Month</p>
                <p className="text-3xl font-bold text-purple-700">{formatCurrency(stats.monthlyEarnings)}</p>
                <p className="text-xs text-purple-500">0.3% of commission</p>
              </div>
              <TrendingUp className="w-10 h-10 text-purple-300" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* My Restaurants */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>My Restaurants</CardTitle>
            <CardDescription>Restaurants you've onboarded to Hambre</CardDescription>
          </div>
          <Link to="/sales/restaurants">
            <Button variant="outline" size="sm">
              View All <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {restaurants.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl">
              <Store className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No restaurants yet</h3>
              <p className="text-slate-500 mb-4">Start onboarding restaurants to earn commission</p>
              <p className="text-sm text-slate-400">Share your referral code: <span className="font-mono font-bold text-purple-600">{user?.referral_code}</span></p>
            </div>
          ) : (
            <div className="grid gap-4">
              {restaurants.slice(0, 5).map(restaurant => (
                <div 
                  key={restaurant.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center border">
                      <Store className="w-6 h-6 text-purple-500" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-900">{restaurant.name}</h4>
                      <p className="text-sm text-slate-500">{restaurant.city} · {restaurant.cuisine_type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">{restaurant.order_count || 0} orders</p>
                    <p className={`text-xs ${restaurant.is_active ? 'text-green-500' : 'text-red-500'}`}>
                      {restaurant.is_active ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="bg-gradient-to-r from-slate-50 to-slate-100">
        <CardHeader>
          <CardTitle>How You Earn</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Onboard Restaurants</h4>
                <p className="text-sm text-slate-500">Help restaurants join Hambre using your referral code</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Restaurants Get Orders</h4>
                <p className="text-sm text-slate-500">When your restaurants receive delivery orders</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-purple-600 font-bold">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Earn 0.3% Commission</h4>
                <p className="text-sm text-slate-500">Lifetime recurring commission on platform fees</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
